import * as j from '@mojotech/json-type-validation';

export const ContactFlowVersion: number = 1 as const;

export const PollyLanguage = {
  ARB: 'arb',
  CMN_CN: 'cmn-CN',
  CY_GB: 'cy-GB',
  DA_DK: 'da-DK',
  DE_DE: 'de-DE',
  EN_AU: 'en-AU',
  EN_GB: 'en-GB',
  EN_GB_WLS: 'en-GB-WLS',
  EN_IN: 'en-IN',
  EN_US: 'en-US',
  ES_ES: 'es-ES',
  ES_MX: 'es-MX',
  ES_US: 'es-US',
  FR_CA: 'fr-CA',
  FR_FR: 'fr-FR',
  FR_BE: 'fr-BE',
  IS: 'is-IS',
  IT: 'it-IT',
  JA: 'ja-JP',
  HI: 'hi-IN',
  KO: 'ko-KR',
  NB: 'nb-NO',
  NL: 'nl-NL',
  PL: 'pl-PL',
  PT_BR: 'pt-BR',
  PT_PT: 'pt-PT',
  RO: 'ro-RO',
  RU: 'ru-RU',
  SV: 'sv-SE',
  TR: 'tr-TR',
  EN_NZ: 'en-NZ',
  EN_ZA: 'en-ZA',
  CA_ES: 'ca-ES',
  DE: 'de-AT',
  YUE: 'yue-CN',
  AR_AE: 'ar-AE',
  FI: 'fi-FI',
  EN_IE: 'en-IE',
  NL_BE: 'nl-BE',
} as const;

export type PollyLanguage = typeof PollyLanguage[keyof typeof PollyLanguage];

export const ContactFlowActionType = {
  // Note: This is not in the backend as it purely used by the UI to work out the startActionId of a contact flow
  Entry: 'entry',
  Answer: 'answer',
  // Not yet implemented in backend
  CheckExclusion: 'check-exclusion',
  Delay: 'delay',
  Enqueue: 'enqueue',
  FetchLead: 'fetch-lead',
  HangUp: 'hang-up',
  // Not yet implemented in backend
  LexBotAudio: 'lex-bot-audio',
  NoOp: 'no-op',
  PlayMessage: 'play',
  SetAttribute: 'set-attribute',
  SetDisposition: 'set-disposition',
  WaitDTMF: 'wait-dtmf',
  // must be end action
  TransferExternal: 'transfer-external',
  UnsetAttribute: 'unset-attribute',
} as const;

export type ContactFlowActionType = typeof ContactFlowActionType[keyof typeof ContactFlowActionType];

export const ParameterClass = {
  CmpContact: 'contact',
  CmpDialler: 'dialler',
  CmpDTMF: 'dtmf',
  CmpLex: 'lex',
  CmpLiteral: 'literal',
  CmpQueue: 'queue',
} as const;

export type ParameterClass = typeof ParameterClass[keyof typeof ParameterClass];

export const CompareOp = {
  OpEquals: '=',
  OpNotEquals: '!=',
  OpEqualsCaseInsensitive: 'ci=',
  OpNotEqualsCaseInsensitive: 'ci!=',
  OpMatch: '~',
  OpMatchCaseInsensitive: 'ci~',
  OpIn: 'in',
  OpContains: 'contains',
  OpLessLexical: 'lexical-<',
  OpGreaterLexical: 'lexical->',
  OpLessEqLexical: 'lexcal-<=',
  OpGreaterEqLexical: 'lexical>=',
  OpLessNumeric: 'numeric-<',
  OpGreaterNumeric: 'numeric->',
  OpLessEqNumeric: 'numeric-<=',
  OpGreaterEqNumeric: 'numeric->=',
} as const;

export type CompareOp = typeof CompareOp[keyof typeof CompareOp];

export type PredictiveAgentStatus = {
  agentStatus: string;
  description: string;
  position: number;
};

export type PredictiveIvrMessage = {
  key: string;
  description: string;
  content?: string;
  filename?: string; // auto-generated field at backend
  audioLocation?: string;
  signedUrl?: string;
  language?: PollyLanguage | null;
  voice?: string | null;
  file?: File; // for uploading only
};

export type SipQueue = {
  queueID: number;
  queueName: string;
  description: string;
};

export type SipQueueResponse = {
  nextPageUrl: string | null;
  queues: SipQueue[];
};

export type NewSipQueue = Omit<SipQueue, 'queueID'>;

export type TrunkFlow = {
  createdBy: string;
  createdTimestamp: string | null;
  lastUpdatedBy: string;
  lastUpdatedTimestamp: string | null;
  trunk: string;
  contactFlowId: number;
  contactFlowName: string;
};

export type UpsertTrunkFlow = Pick<TrunkFlow, 'trunk' | 'contactFlowId'>;

// Not yet implemented
export type ParameterCheckExclusion = {};

export type ParameterDelay = {
  delayMS: number;
};

export type ParameterEnqueue = {
  queueId: number;
  csat?: boolean;
};

export type ParameterFetchLead = {
  campaigns: number[];
  defaultCampaign: number;
};

export type ParameterHangup = {
  reasonExcluded: boolean;
};

// Not yet implemented
export type ParameterLexBotAudio = {};

export type ParameterPlayMessage = {
  messageKey: string;
  cancelOnDTMF?: boolean;
  dontClearDTMF?: boolean;
};

export type ParameterSetAttribute = {
  name: string;
  // ParameterClass
  class: string;
  field: string;
};

export type ParameterSetDispositionCallback = {
  // ParameterClass
  endpointSourceClass: string;
  endpointSourceField: string;
};

export type ParameterSetDisposition = {
  code: string;
  subCode: string;
  exclusionList: string | null;
  callback: ParameterSetDispositionCallback | null;
};

export type ParameterWaitDTMF = {
  digits: number;
  timeoutMS: number;
};

export type ParameterTransferExternal = {
  endpoint?: string;
  fromAttribute?: string;
  timeoutSecond?: number;
  timeoutMessageKey: string;
};

export type ParameterUnsetAttribute = {
  name: string;
};

export type ContactFlowParameter = {
  checkExclusion?: ParameterCheckExclusion;
  delay?: ParameterDelay;
  enqueue?: ParameterEnqueue;
  fetchLead?: ParameterFetchLead;
  hangup?: ParameterHangup;
  lexBotAudio?: ParameterLexBotAudio;
  playMessage?: ParameterPlayMessage;
  setAttribute?: ParameterSetAttribute;
  setDisposition?: ParameterSetDisposition;
  waitDTMF?: ParameterWaitDTMF;
  transferExternal?: ParameterTransferExternal;
  unsetAttribute?: ParameterUnsetAttribute;
};

export type Selector = {
  // ParameterClass
  class: string;
  field: string;
  // CompareOp
  operation: string;
  value: string;
  hasError?: boolean;
};

export type ActionRoute = {
  selector?: Selector;
  actionId: string;
};

export type NextAction = {
  routes: ActionRoute[];
};

export type ContactFlowAction = {
  id: string;
  // ContactFlowActionType
  type: string;
  description: string;
  parameter: ContactFlowParameter;
  next?: NextAction;
};

export type ContactFlow = {
  createdBy: string;
  createdTimestamp: string | null;
  lastUpdatedBy: string;
  lastUpdatedTimestamp: string | null;
  contactFlowId: number;
  version: number;
  name: string;
  description: string;
  startActionId: string;
  tags: string;
  actions: ContactFlowAction[];
};

const GetPredictiveIvrMessageResponseDecoder: j.Decoder<PredictiveIvrMessage> = j
  .object({
    key: j.string(),
    content: j.union(j.string(), j.constant(null)),
    description: j.string(),
    filename: j.string(),
    audio_location: j.string(),
    signed_url: j.string(),
    polly_language: j.union(j.string(), j.constant(null)),
    polly_voice: j.union(j.string(), j.constant(null)),
  })
  .map((item: any) => ({
    key: item.key,
    content: item.content || undefined,
    description: item.description,
    filename: item.filename,
    audioLocation: item.audio_location,
    signedUrl: item.signed_url,
    language: item.polly_language || undefined,
    voice: item.polly_voice || undefined,
  }));

export const GetPredictiveIvrMessagesResponseDecoder: j.Decoder<PredictiveIvrMessage[]> = j
  .object({
    messages: j.union(j.array(GetPredictiveIvrMessageResponseDecoder), j.constant(null)),
  })
  .map((item) => item.messages || []);

const GetPredictiveAgentStatusListDecoder: j.Decoder<PredictiveAgentStatus> = j
  .object({
    agent_status: j.string(),
    description: j.string(),
    position: j.number(),
  })
  .map(
    (item): PredictiveAgentStatus => ({
      agentStatus: item.agent_status,
      description: item.description,
      position: item.position,
    }),
  );

export const GetPredictiveAgentStatusesDecoder: j.Decoder<PredictiveAgentStatus[]> = j
  .object({
    statuses: j.union(j.array(GetPredictiveAgentStatusListDecoder), j.constant(null)),
  })
  .map((resp) => resp.statuses ?? []);

const GetSipQueueResponseDecoder: j.Decoder<SipQueue> = j
  .object({
    queue_id: j.number(),
    queue_name: j.string(),
    description: j.string(),
  })
  .map((item) => ({
    queueID: item.queue_id,
    queueName: item.queue_name,
    description: item.description,
  }));

export const GetSipQueuesResponseDecoder: j.Decoder<SipQueueResponse> = j
  .object({
    next_page_url: j.union(j.string(), j.constant(null)),
    queues: j.array(GetSipQueueResponseDecoder),
  })
  .map((item) => ({
    nextPageUrl: item.next_page_url,
    queues: item.queues,
  }));

export const GetTrunkFlowResponseDecoder: j.Decoder<TrunkFlow> = j
  .object({
    created_by: j.string(),
    created_timestamp: j.union(j.string(), j.constant(null)),
    last_updated_by: j.string(),
    last_updated_timestamp: j.union(j.string(), j.constant(null)),
    trunk: j.string(),
    contact_flow_id: j.number(),
    contact_flow_name: j.string(),
  })
  .map((item) => ({
    createdBy: item.created_by,
    createdTimestamp: item.created_timestamp,
    lastUpdatedBy: item.last_updated_by,
    lastUpdatedTimestamp: item.last_updated_timestamp,
    trunk: item.trunk,
    contactFlowId: item.contact_flow_id,
    contactFlowName: item.contact_flow_name,
  }));

export const GetTrunkFlowsResponseDecoder: j.Decoder<TrunkFlow[]> = j
  .object({
    trunk_flows: j.array(GetTrunkFlowResponseDecoder),
  })
  .map((item) => item.trunk_flows);

export const GetParameterCheckExclusionDecoder: j.Decoder<ParameterCheckExclusion> = j.object({});

export const GetParameterDelayDecoder: j.Decoder<ParameterDelay> = j
  .object({
    delay_ms: j.number(),
  })
  .map((item) => ({ delayMS: item.delay_ms }));

export const GetParameterEnqueueDecoder: j.Decoder<ParameterEnqueue> = j
  .object({
    queue_id: j.number(),
    csat: j.optional(j.boolean()),
  })
  .map((item) => ({
    queueId: item.queue_id,
    csat: item.csat,
  }));

export const GetParameterFetchLeadDecoder: j.Decoder<ParameterFetchLead> = j
  .object({
    campaigns: j.array(j.number()),
    default_campaign: j.number(),
  })
  .map((item) => ({
    campaigns: item.campaigns,
    defaultCampaign: item.default_campaign,
  }));

export const GetParameterHangupDecoder: j.Decoder<ParameterHangup> = j
  .object({
    reason_excluded: j.boolean(),
  })
  .map((item) => ({
    reasonExcluded: item.reason_excluded,
  }));

export const GetParameterLexBotAudioDecoder: j.Decoder<ParameterLexBotAudio> = j.object({});

export const GetParameterPlayMessageDecoder: j.Decoder<ParameterPlayMessage> = j
  .object({
    message_key: j.string(),
    cancel_on_dtmf: j.optional(j.boolean()),
    dont_clear_dtmf: j.optional(j.boolean()),
  })
  .map((item) => ({
    messageKey: item.message_key,
    cancelOnDTMF: item.cancel_on_dtmf,
    dontClearDTMF: item.dont_clear_dtmf,
  }));

export const GetParameterSetAttributeDecoder: j.Decoder<ParameterSetAttribute> = j
  .object({
    name: j.string(),
    class: j.string(),
    field: j.string(),
  })
  .map((item) => ({
    name: item.name,
    class: item.class,
    field: item.field,
  }));

export const GetParameterSetDispositionCallbackDecoder: j.Decoder<ParameterSetDispositionCallback> = j
  .object({
    endpoint_class: j.string(),
    endpoint_field: j.string(),
  })
  .map((item) => ({
    endpointSourceClass: item.endpoint_class,
    endpointSourceField: item.endpoint_field,
  }));

export const GetParameterSetDispositionDecoder: j.Decoder<ParameterSetDisposition> = j
  .object({
    code: j.string(),
    sub_code: j.string(),
    exclusion_list: j.union(j.string(), j.constant(null)),
    callback: j.union(GetParameterSetDispositionCallbackDecoder, j.constant(null)),
  })
  .map((item) => ({
    code: item.code,
    subCode: item.sub_code,
    exclusionList: item.exclusion_list || null,
    callback: item.callback || null,
  }));

export const GetParameterWaitDTMFDecoder: j.Decoder<ParameterWaitDTMF> = j
  .object({
    digits_to_collect: j.number(),
    timeout_ms: j.number(),
  })
  .map((item) => ({
    digits: item.digits_to_collect,
    timeoutMS: item.timeout_ms,
  }));

export const GetParameterTransferExternalDecoder: j.Decoder<ParameterTransferExternal> = j
  .object({
    endpoint: j.optional(j.string()),
    from_attribute: j.optional(j.string()),
    timeout_second: j.optional(j.number()),
    timeout_message_key: j.string(),
  })
  .map((item) => ({
    endpoint: item.endpoint || undefined,
    fromAttribute: item.from_attribute || undefined,
    timeoutSecond: item.timeout_second || undefined,
    timeoutMessageKey: item.timeout_message_key,
  }));

export const GetParameterUnsetAttributeDecoder: j.Decoder<ParameterUnsetAttribute> = j.object({
  name: j.string(),
});

export const GetContactFlowParameterDecoder: j.Decoder<ContactFlowParameter> = j
  .object({
    check_exclusion: j.optional(GetParameterCheckExclusionDecoder),
    delay: j.optional(GetParameterDelayDecoder),
    enqueue: j.optional(GetParameterEnqueueDecoder),
    fetch_lead: j.optional(GetParameterFetchLeadDecoder),
    hang_up: j.optional(GetParameterHangupDecoder),
    lex_bot_audio: j.optional(GetParameterLexBotAudioDecoder),
    play_message: j.optional(GetParameterPlayMessageDecoder),
    set_attribute: j.optional(GetParameterSetAttributeDecoder),
    set_disposition: j.optional(GetParameterSetDispositionDecoder),
    wait_dtmf: j.optional(GetParameterWaitDTMFDecoder),
    transfer_external: j.optional(GetParameterTransferExternalDecoder),
    unset_attribute: j.optional(GetParameterUnsetAttributeDecoder),
  })
  .map((item) => ({
    checkExclusion: item.check_exclusion || undefined,
    delay: item.delay || undefined,
    enqueue: item.enqueue || undefined,
    fetchLead: item.fetch_lead || undefined,
    hangup: item.hang_up || undefined,
    lexBotAudio: item.lex_bot_audio || undefined,
    playMessage: item.play_message || undefined,
    setAttribute: item.set_attribute || undefined,
    setDisposition: item.set_disposition || undefined,
    waitDTMF: item.wait_dtmf || undefined,
    transferExternal: item.transfer_external || undefined,
    unsetAttribute: item.unset_attribute || undefined,
  }));

export const GetSelectorDecoder: j.Decoder<Selector> = j
  .object({
    class: j.string(),
    field: j.string(),
    operation: j.string(),
    value: j.string(),
    has_error: j.optional(j.boolean()),
  })
  .map((item) => ({
    class: item.class,
    field: item.field,
    operation: item.operation,
    value: item.value,
    hasError: item.has_error || undefined,
  }));

export const GetActionRouteDecoder: j.Decoder<ActionRoute> = j
  .object({
    selector: j.optional(GetSelectorDecoder),
    action_id: j.string(),
  })
  .map((item) => ({
    selector: item.selector || undefined,
    actionId: item.action_id,
  }));

export const GetNextActionDecoder: j.Decoder<NextAction> = j
  .object({
    routes: j.array(GetActionRouteDecoder),
  })
  .map((item) => ({
    routes: item.routes,
  }));

export const GetContactFlowActionsDecoder: j.Decoder<ContactFlowAction> = j
  .object({
    id: j.string(),
    type: j.string(),
    description: j.string(),
    parameter: GetContactFlowParameterDecoder,
    next: j.optional(GetNextActionDecoder),
  })
  .map((item) => ({
    id: item.id,
    type: item.type,
    description: item.description,
    parameter: item.parameter,
    next: item.next || undefined,
  }));

export const GetContactFlowDecoder: j.Decoder<ContactFlow> = j
  .object({
    created_by: j.string(),
    created_timestamp: j.union(j.string(), j.constant(null)),
    last_updated_by: j.string(),
    last_updated_timestamp: j.union(j.string(), j.constant(null)),
    contact_flow_id: j.number(),
    version: j.number(),
    name: j.string(),
    description: j.string(),
    start_action_id: j.string(),
    tags: j.string(),
    actions: j.array(GetContactFlowActionsDecoder),
  })
  .map((item) => ({
    createdBy: item.created_by,
    createdTimestamp: item.created_timestamp || null,
    lastUpdatedBy: item.last_updated_by,
    lastUpdatedTimestamp: item.last_updated_timestamp || null,
    contactFlowId: item.contact_flow_id,
    version: item.version,
    name: item.name,
    description: item.description,
    startActionId: item.start_action_id,
    tags: item.tags,
    actions: item.actions,
  }));

export const GetContactFlowsResponseDecoder: j.Decoder<ContactFlow[]> = j
  .object({
    flows: j.array(GetContactFlowDecoder),
  })
  .map((item) => item.flows);

export const GetArchivedContactFlowsResponseDecoder: j.Decoder<ContactFlow[]> = j.array(GetContactFlowDecoder);

export type ContactFlowInfo = Pick<ContactFlow, 'name' | 'description' | 'tags' | 'version'>;
export type ContactFlowActionsSummary = Pick<ContactFlow, 'startActionId' | 'actions'>;
export type ContactFlowData = ContactFlowInfo & ContactFlowActionsSummary;

// Not yet implemented
export type ParameterCheckExclusionEncoded = {};

export type ParameterDelayEncoded = {
  delay_ms: number;
};

export type ParameterEnqueueEncoded = {
  queue_id: number;
  csat?: boolean;
};

export type ParameterFetchLeadEncoded = {
  campaigns: number[];
  default_campaign: number;
};

export type ParameterHangupEncoded = {
  reason_excluded: boolean;
};

// Not yet implemented
export type ParameterLexBotAudioEncoded = {};

export type ParameterPlayMessageEncoded = {
  message_key: string;
  cancel_on_dtmf?: boolean;
  dont_clear_on_dtmf?: boolean;
};

export type ParameterSetAttributeEncoded = {
  name: string;
  // ParameterClass
  class: string;
  field: string;
};

export type ParameterSetDispositionCallbackEncoded = {
  // ParameterClass
  endpoint_class: string;
  endpoint_field: string;
};

export type ParameterSetDispositionEncoded = {
  code: string;
  sub_code: string;
  exclusion_list: string | null;
  callback: ParameterSetDispositionCallbackEncoded | null;
};

export type ParameterWaitDTMFEncoded = {
  digits_to_collect: number;
  timeout_ms: number;
};

export type ParameterTransferExternalEncoded = {
  endpoint?: string;
  from_attribute?: string;
  timeout_second?: number;
  timeout_message_key: string;
};

export type ParameterUnsetAttributeEncoded = {
  name: string;
};

type ContactFlowParameterEncoded = {
  check_exclusion?: ParameterCheckExclusionEncoded;
  delay?: ParameterDelayEncoded;
  enqueue?: ParameterEnqueueEncoded;
  fetch_lead?: ParameterFetchLeadEncoded;
  hang_up?: ParameterHangupEncoded;
  lex_bot_audio?: ParameterLexBotAudioEncoded;
  play_message?: ParameterPlayMessageEncoded;
  set_attribute?: ParameterSetAttributeEncoded;
  set_disposition?: ParameterSetDispositionEncoded;
  wait_dtmf?: ParameterWaitDTMFEncoded;
  transfer_external?: ParameterTransferExternalEncoded;
  unset_attribute?: ParameterUnsetAttributeEncoded;
};

export type SelectorEncoded = {
  // ParameterClass
  class: string;
  field: string;
  // CompareOp
  operation: string;
  value: string;
  has_error?: boolean;
};

export type ActionRouteEncoded = {
  selector?: SelectorEncoded;
  action_id: string;
};

export type NextActionEncoded = {
  routes: ActionRouteEncoded[];
};

type ContactFlowActionEncoded = {
  id: string;
  // ContactFlowActionType
  type: string;
  description: string;
  parameter: ContactFlowParameterEncoded;
  next?: NextActionEncoded;
};
type ContactFlowDataEncoded = {
  version: number;
  name: string;
  description: string;
  start_action_id: string;
  tags: string;
  actions: ContactFlowActionEncoded[];
};

export const GetParameterCheckExclusionEncoder: j.Decoder<ParameterCheckExclusionEncoded> = j.object({});

export const GetParameterDelayEncoded: j.Decoder<ParameterDelayEncoded> = j
  .object({
    delayMS: j.number(),
  })
  .map((item) => ({ delay_ms: item.delayMS }));

export const GetParameterEnqueueEncoder: j.Decoder<ParameterEnqueueEncoded> = j
  .object({
    queueId: j.number(),
    csat: j.optional(j.boolean()),
  })
  .map((item) => ({
    queue_id: item.queueId,
    csat: item.csat,
  }));

export const GetParameterFetchLeadEncoder: j.Decoder<ParameterFetchLeadEncoded> = j
  .object({
    campaigns: j.array(j.number()),
    defaultCampaign: j.number(),
  })
  .map((item) => ({
    campaigns: item.campaigns,
    default_campaign: item.defaultCampaign,
  }));

export const GetParameterHangupEncoder: j.Decoder<ParameterHangupEncoded> = j
  .object({
    reasonExcluded: j.boolean(),
  })
  .map((item) => ({
    reason_excluded: item.reasonExcluded,
  }));

export const GetParameterLexBotAudioEncoder: j.Decoder<ParameterLexBotAudioEncoded> = j.object({});

export const GetParameterPlayMessageEncoder: j.Decoder<ParameterPlayMessageEncoded> = j
  .object({
    messageKey: j.string(),
    cancelOnDTMF: j.optional(j.boolean()),
    dontClearDTMF: j.optional(j.boolean()),
  })
  .map((item) => ({
    message_key: item.messageKey,
    cancel_on_dtmf: item.cancelOnDTMF,
    dont_clear_dtmf: item.dontClearDTMF,
  }));

export const GetParameterSetAttributeEncoder: j.Decoder<ParameterSetAttributeEncoded> = j
  .object({
    name: j.string(),
    class: j.string(),
    field: j.string(),
  })
  .map((item) => ({
    name: item.name,
    class: item.class,
    field: item.field,
  }));

export const GetParameterSetDispositionCallbackEncoder: j.Decoder<ParameterSetDispositionCallbackEncoded> = j
  .object({
    endpointSourceClass: j.string(),
    endpointSourceField: j.string(),
  })
  .map((item) => ({
    endpoint_class: item.endpointSourceClass,
    endpoint_field: item.endpointSourceField,
  }));

export const GetParameterSetDispositionEncoder: j.Decoder<ParameterSetDispositionEncoded> = j
  .object({
    code: j.string(),
    subCode: j.string(),
    exclusionList: j.union(j.string(), j.constant(null)),
    callback: j.union(GetParameterSetDispositionCallbackEncoder, j.constant(null)),
  })
  .map((item) => ({
    code: item.code,
    sub_code: item.subCode,
    exclusion_list: item.exclusionList || null,
    callback: item.callback || null,
  }));

export const GetParameterWaitDTMFEncoder: j.Decoder<ParameterWaitDTMFEncoded> = j
  .object({
    digits: j.number(),
    timeoutMS: j.number(),
  })
  .map((item) => ({
    digits_to_collect: item.digits,
    timeout_ms: item.timeoutMS,
  }));

export const GetParameterTransferExternalEncoder: j.Decoder<ParameterTransferExternalEncoded> = j
  .object({
    endpoint: j.optional(j.string()),
    fromAttribute: j.optional(j.string()),
    timeoutSecond: j.optional(j.number()),
    timeoutMessageKey: j.string(),
  })
  .map((item) => ({
    endpoint: item.endpoint || undefined,
    from_attribute: item.fromAttribute || undefined,
    timeout_second: item.timeoutSecond || undefined,
    timeout_message_key: item.timeoutMessageKey,
  }));

export const GetParameterUnsetAttributeEncoded: j.Decoder<ParameterUnsetAttributeEncoded> = j.object({
  name: j.string(),
});

export const GetContactFlowParameterEncoder: j.Decoder<ContactFlowParameterEncoded> = j
  .object({
    checkExclusion: j.optional(GetParameterCheckExclusionEncoder),
    delay: j.optional(GetParameterDelayEncoded),
    enqueue: j.optional(GetParameterEnqueueEncoder),
    fetchLead: j.optional(GetParameterFetchLeadEncoder),
    hangup: j.optional(GetParameterHangupEncoder),
    lexBotAudio: j.optional(GetParameterLexBotAudioEncoder),
    playMessage: j.optional(GetParameterPlayMessageEncoder),
    setAttribute: j.optional(GetParameterSetAttributeEncoder),
    setDisposition: j.optional(GetParameterSetDispositionEncoder),
    waitDTMF: j.optional(GetParameterWaitDTMFEncoder),
    transferExternal: j.optional(GetParameterTransferExternalEncoder),
    unsetAttribute: j.optional(GetParameterUnsetAttributeEncoded),
  })
  .map((item) => ({
    check_exclusion: item.checkExclusion || undefined,
    delay: item.delay || undefined,
    enqueue: item.enqueue || undefined,
    fetch_lead: item.fetchLead || undefined,
    hang_up: item.hangup || undefined,
    lex_bot_audio: item.lexBotAudio || undefined,
    play_message: item.playMessage || undefined,
    set_attribute: item.setAttribute || undefined,
    set_disposition: item.setDisposition || undefined,
    wait_dtmf: item.waitDTMF || undefined,
    transfer_external: item.transferExternal || undefined,
    unset_attribute: item.unsetAttribute || undefined,
  }));

export const GetSelectorEncoder: j.Decoder<SelectorEncoded> = j
  .object({
    class: j.string(),
    field: j.string(),
    operation: j.string(),
    value: j.string(),
    hasError: j.optional(j.boolean()),
  })
  .map((item) => ({
    class: item.class,
    field: item.field,
    operation: item.operation,
    value: item.value,
    has_error: item.hasError || undefined,
  }));

export const GetActionRouteEncoder: j.Decoder<ActionRouteEncoded> = j
  .object({
    selector: j.optional(GetSelectorEncoder),
    actionId: j.string(),
  })
  .map((item) => ({
    selector: item.selector || undefined,
    action_id: item.actionId,
  }));

export const GetNextActionEncoder: j.Decoder<NextActionEncoded> = j
  .object({
    routes: j.array(GetActionRouteEncoder),
  })
  .map((item) => ({
    routes: item.routes,
  }));

export const GetContactFlowActionsEncoder: j.Decoder<ContactFlowActionEncoded> = j
  .object({
    id: j.string(),
    type: j.string(),
    description: j.string(),
    parameter: GetContactFlowParameterEncoder,
    next: j.optional(GetNextActionEncoder),
  })
  .map((item) => ({
    id: item.id,
    type: item.type,
    description: item.description,
    parameter: item.parameter,
    next: item.next || undefined,
  }));

export const GetContactFlowDataEncoder: j.Decoder<ContactFlowDataEncoded> = j
  .object({
    name: j.string(),
    description: j.string(),
    startActionId: j.string(),
    tags: j.string(),
    version: j.number(),
    actions: j.array(GetContactFlowActionsEncoder),
  })
  .map((item) => ({
    version: item.version,
    name: item.name,
    description: item.description,
    start_action_id: item.startActionId,
    tags: item.tags,
    actions: item.actions,
  }));

export const GetContactFlowDataDecoder: j.Decoder<ContactFlowData> = j
  .object({
    version: j.constant(ContactFlowVersion),
    name: j.string(),
    description: j.string(),
    start_action_id: j.string(),
    tags: j.string(),
    actions: j.array(GetContactFlowActionsDecoder),
  })
  .map((item) => ({
    version: item.version,
    name: item.name,
    description: item.description,
    startActionId: item.start_action_id,
    tags: item.tags,
    actions: item.actions,
  }));
